import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Select, Space, notification } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { ItemFormValues, PolicyData, RewardItem, RewardRow } from '../types';
import { ITEM_TYPES, ITEM_TYPE_LABEL, itemPayload, presetButtons, rewardErrorMessage } from '../utils';

interface Props {
  open: boolean;
  month: string;
  initial: RewardItem | null;
  presetUid: string | null;
  isAdmin: boolean;
  creators: RewardRow[];
  presets?: PolicyData['presets'];
  onClose: () => void;
  onSaved: () => void;
}

/** Thêm / sửa một khoản nhập tay (docs/thuong-online/02 §3.6). */
const ItemFormModal = ({ open, month, initial, presetUid, isAdmin, creators, presets, onClose, onSaved }: Props) => {
  const [form] = Form.useForm<ItemFormValues>();
  const [saving, setSaving] = useState(false);
  const type = Form.useWatch('type', form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      initial
        ? {
            tiktokUid: initial.tiktokUid,
            type: initial.type,
            amount: initial.amount,
            kc: initial.kc,
            deductDays: initial.deductDays,
            deductHours: initial.deductSeconds ? initial.deductSeconds / 3600 : null,
            eventDate: initial.eventDate ? dayjs(initial.eventDate) : null,
            note: initial.note || '',
            evidenceUrl: initial.evidenceUrl || '',
          }
        : { tiktokUid: presetUid || undefined, type: 'PKQT' }
    );
  }, [open, initial, presetUid, form]);

  const options = useMemo(
    () => creators.map((r) => ({ value: r.uid, label: `@${r.tiktokUsername || r.uid} · ${r.uid}` })),
    [creators]
  );
  const buttons = presetButtons(type, presets);
  const deduct = type === 'LIVE_DEDUCT';
  const noMoney = deduct || type === 'VPNT';

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const body = itemPayload(values, month);
      if (initial) await AxiosUtils.put(`${API.onlineRewardItems}/${initial.id}`, body);
      else await AxiosUtils.post(API.onlineRewardItems, body);
      notification.success({ message: initial ? 'Đã sửa khoản' : 'Đã thêm khoản' });
      onSaved();
    } catch (err) {
      notification.error({ message: 'Chưa lưu được', description: rewardErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} title={initial ? 'Sửa khoản nhập tay' : 'Thêm khoản nhập tay'} onCancel={onClose} onOk={submit} okText="Lưu" cancelText="Huỷ" confirmLoading={saving} destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="tiktokUid" label="Creator" rules={[{ required: true, message: 'Chọn Creator hoặc dán UID' }]}>
          <Select
            showSearch
            disabled={!!initial}
            placeholder="Tìm @TikTok ID hoặc dán UID (19 số)"
            options={options}
            filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.replace(/^@/, '').toLowerCase())}
            notFoundContent="Không thấy trong bảng tháng này — dán UID rồi bấm Enter"
            onSearch={(v) => /^\d{5,32}$/.test(v.trim()) && form.setFieldValue('tiktokUid', v.trim())}
          />
        </Form.Item>
        <Form.Item name="type" label="Loại" rules={[{ required: true }]}>
          <Select options={ITEM_TYPES.map((t) => ({ value: t, label: ITEM_TYPE_LABEL[t] }))} />
        </Form.Item>
        {buttons.length > 0 && (
          <Space wrap style={{ marginBottom: 12 }}>
            {buttons.map((b) => (
              <Button key={b.label} size="small" onClick={() => form.setFieldsValue({ amount: b.amount, kc: b.kc ?? undefined })}>
                {b.label}
              </Button>
            ))}
          </Space>
        )}
        {!noMoney && (
          <Form.Item name="amount" label="Số tiền (đ)" rules={[{ required: true, message: 'Nhập số tiền' }]}>
            <InputNumber min={0} step={10000} style={{ width: '100%' }} formatter={(v) => String(v ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={(v) => Number(String(v || '').replace(/\D/g, '')) as any} />
          </Form.Item>
        )}
        {deduct && (
          <Space>
            <Form.Item name="deductDays" label="Trừ số ngày">
              <InputNumber min={0} max={31} />
            </Form.Item>
            <Form.Item name="deductHours" label="Trừ số giờ">
              <InputNumber min={0} step={0.5} />
            </Form.Item>
          </Space>
        )}
        {!noMoney && (
          <Form.Item name="kc" label="KC (nếu có)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item name="eventDate" label="Ngày diễn ra">
          <DatePicker format="DD/MM/YYYY" disabledDate={(d) => d.format('YYYY-MM') !== month} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="note"
          label={type === 'VPNT' ? 'Nội dung vi phạm' : 'Ghi chú / lý do'}
          rules={[{ required: ['VPNT', 'LIVE_DEDUCT', 'OTHER_BONUS', 'NEW_CREATOR_OVERRIDE'].includes(type), message: 'Cần ghi nội dung' }]}
        >
          <Input.TextArea rows={2} maxLength={500} showCount />
        </Form.Item>
        <Form.Item name="evidenceUrl" label="Link minh chứng">
          <Input placeholder="https://…" />
        </Form.Item>
        {isAdmin && !initial && (
          <Form.Item name="confirmNow" valuePropName="checked">
            <Checkbox>Xác nhận luôn (Admin)</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ItemFormModal;
